import * as React from 'react'
import { View, ScrollView, ScrollViewProps } from 'react-native'
import styled from '@theme/styled-components'

import Header from './Header'
import Drawer from './Drawer'
import Loader from '@components/loader/Loader'

import { menu } from '@layout/common'

import useReducer from '@store/useReducer'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'

import useBreakpoint from '@utils/useBreakpoints'

interface Props extends ScrollViewProps {
  children?: any
  centered?: boolean
  fixed?: boolean
  loading?: boolean
  header?: string
  left?: IconName
  onLeft?: () => void
  right?: IconName
  rightText?: string
  onRight?: () => void
  renderHeader?: () => React.ReactNode // override the entire Header
  renderSubHeader?: () => React.ReactNode // add content inside Header at bottom
  renderFixedBottom?: () => React.ReactNode
}

const Content = ({
  children,
  centered,
  fixed,
  renderFixedBottom,
  loading = false,
  header,
  left,
  onLeft,
  right,
  rightText,
  onRight,
  renderHeader,
  renderSubHeader,
  ...rest
}: Props) => {
  const renderHeaderComponent = () => {
    if (renderHeader) {
      return renderHeader()
    }

    if (!!header || onLeft || right || !!renderSubHeader) {
      return (
        <Header
          onLeft={onLeft}
          title={header}
          left={left}
          right={right}
          onRight={onRight}
          renderSub={renderSubHeader}
          rightText={rightText}
        />
      )
    }

    return null
  }

  if (loading) {
    return (
      <>
        {renderHeaderComponent()}
        <CenteredContainer>
          <Loader />
        </CenteredContainer>
      </>
    )
  }

  if (centered) {
    return (
      <>
        {renderHeaderComponent()}
        <CenteredContainer>{children}</CenteredContainer>
        {!!renderFixedBottom ? renderFixedBottom() : null}
      </>
    )
  }

  if (fixed) {
    return (
      <>
        {renderHeaderComponent()}
        <Container>{children}</Container>
        {!!renderFixedBottom ? renderFixedBottom() : null}
      </>
    )
  }

  return (
    <>
      {renderHeaderComponent()}
      <ScrollContainer
        scrollIndicatorInsets={{ right: 1 }}
        bounces={false}
        keyboardShouldPersistTaps="handled"
        {...rest}>
        {children}
      </ScrollContainer>
      {!!renderFixedBottom ? renderFixedBottom() : null}
    </>
  )
}

const Root = (props: Props) => {
  const [, bp] = useBreakpoint()

  const user = useReducer(UserStore.store, (s) => s.user)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  if (!user || (user.typeUtilisateur === 'EXPOSANT' && bp !== 'phone') || !menu.find((m) => scopes.includes(m))) {
    // full content
    return (
      <MainContainer>
        <Content {...props} />
      </MainContainer>
    )
  }

  return (
    <DrawerContainer>
      <MainContainer>
        <Drawer>
          <Content {...props} />
        </Drawer>
      </MainContainer>
    </DrawerContainer>
  )
}

export default Root

const DrawerContainer = styled(View)`
  flex: 1;
  flex-direction: row;
`

const MainContainer = styled(View)`
  flex: 1;
  flex-direction: column;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.backgroundSecondary : props.theme.colors.background};
`

const CenteredContainer = styled(View)`
  align-items: center;
  justify-content: center;
  flex: 1;
  flex-direction: column;
  z-index: 0;
`

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
  z-index: 0;
`

const Container = styled(View)`
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  z-index: 0;
`
